import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import ZodiacSignModal from './ZodiacSignModal';

// Fallback EN→PT when AI returns English names
const planetNameMap = {
  Sun: 'Sol', Moon: 'Lua', Mercury: 'Mercúrio', Venus: 'Vênus', Mars: 'Marte',
  Jupiter: 'Júpiter', Saturn: 'Saturno', Uranus: 'Urano', Neptune: 'Netuno', Pluto: 'Plutão',
};

const signNameMap = {
  Aries: 'Áries', Taurus: 'Touro', Gemini: 'Gêmeos', Cancer: 'Câncer', Leo: 'Leão',
  Virgo: 'Virgem', Libra: 'Libra', Scorpio: 'Escorpião', Sagittarius: 'Sagitário',
  Capricorn: 'Capricórnio', Aquarius: 'Aquário', Pisces: 'Peixes',
};

const toPT = (name, map) => map[name] || name;

const zodiacIcons = {
  'Áries': '♈',
  'Touro': '♉',
  'Gêmeos': '♊',
  'Câncer': '♋',
  'Leão': '♌',
  'Virgem': '♍',
  'Libra': '♎',
  'Escorpião': '♏',
  'Sagitário': '♐',
  'Capricórnio': '♑',
  'Aquário': '♒',
  'Peixes': '♓'
};

const planetSymbols = {
  'Sol': '☉',
  'Lua': '☽',
  'Mercúrio': '☿',
  'Vênus': '♀',
  'Marte': '♂',
  'Júpiter': '♃',
  'Saturno': '♄',
  'Urano': '⛢',
  'Netuno': '♆',
  'Plutão': '♇'
};

const SkeletonPlaneta = () => (
  <div className="p-4 rounded-lg">
    <div className="animate-pulse flex gap-4">
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-purple-100 rounded"></div>
        <div className="w-8 h-8 bg-purple-100 rounded"></div>
      </div>
      <div className="flex-1 space-y-2">
        <div className="h-4 w-24 bg-purple-100 rounded"></div>
        <div className="h-3 w-32 bg-purple-100 rounded"></div>
      </div>
    </div>
  </div>
);

const PlanetasSection = ({ loadingAnalysis, analysis }) => {
  const [selectedSign, setSelectedSign] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedPlanet, setExpandedPlanet] = useState(null);

  const handleSignClick = (e, sign) => {
    e.stopPropagation();
    setSelectedSign(sign);
    setIsModalOpen(true);
  };

  const togglePlanet = (index) => {
    setExpandedPlanet((prev) => (prev === index ? null : index));
  };

  if (loadingAnalysis) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 mb-8">
        <div className="h-8 w-48 bg-purple-100 rounded mb-6 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonPlaneta key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (!analysis?.Relatorio?.PosicaoDosPlanetas) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 mb-8"
      >
        <h2 className="font-serif text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
          Posição dos Planetas
        </h2>
        <div className="space-y-1">
          {analysis.Relatorio.PosicaoDosPlanetas.map((planeta, index) => {
            const nomePlaneta = toPT(planeta.Planeta || planeta.name, planetNameMap);
            const nomeSigno = toPT(planeta.Signo || planeta.sign, signNameMap);
            const isExpanded = expandedPlanet === index;
            const house = planeta.house || planeta.Casa;
            const degree = planeta.degree || planeta.Grau;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="rounded-lg border border-purple-100 overflow-hidden"
              >
                {/* Collapsed header - always visible */}
                <button
                  type="button"
                  onClick={() => togglePlanet(index)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-purple-50 transition-colors text-left cursor-pointer"
                >
                  <div className="flex items-center gap-2 min-w-[4rem]">
                    <span className="text-2xl">{planetSymbols[nomePlaneta] || '✨'}</span>
                    <span
                      className="text-2xl cursor-pointer hover:opacity-75"
                      onClick={(e) => handleSignClick(e, nomeSigno)}
                    >
                      {zodiacIcons[nomeSigno] || '✨'}
                    </span>
                  </div>

                  <div className="flex-1 flex items-center gap-2 flex-wrap">
                    <span className="font-serif font-medium text-gray-800">{nomePlaneta}</span>
                    <span className="text-sm text-gray-500">em</span>
                    <span className="font-serif font-medium text-gray-800">{nomeSigno}</span>
                    {house && (
                      <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-600 text-xs font-medium">
                        Casa {String(house).replace(/^Casa\s*/i, '')}
                      </span>
                    )}
                    {degree && (
                      <span className="text-xs text-gray-400">{degree}</span>
                    )}
                  </div>

                  <motion.div
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0 text-purple-400"
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </button>

                {/* Expandable interpretation */}
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      key="content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-1 ml-[4.5rem]">
                        <p className="font-body text-sm text-gray-600 leading-relaxed">
                          {planeta.Interpretacao}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      <ZodiacSignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        sign={selectedSign}
        signData={Array.isArray(analysis?.Relatorio?.Signos)
          ? analysis?.Relatorio?.Signos?.find(s => s.Nome === selectedSign)
          : null
        }
      />
    </>
  );
};

export default PlanetasSection;
